import { useEffect, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { ReactComponent as TaskAdminIcon } from 'icons/task-admin-filled-icon.svg';
import { PaginationTable } from 'components/Global';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import ActionButtons from 'components/Global/Table/ActionButtons';
import EmptyState from 'components/Global/EmptyState';
import { getAllApprovalData } from 'features/approvals/actions';
import StatusButton from 'components/Global/Table/StatusButton';
import LoadingSpinner from 'components/Global/LoadingSpinner';
import { ReactComponent as DetailDisabledIcon } from 'icons/detail-disabled-icon.svg';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

const column = [
  { header: 'No.', value: 'no' },
  { header: 'Nama Lengkap', value: 'user_name' },
  { header: 'Task', value: 'name' },
  { header: 'Status Sewa', value: 'status' },
];

const CustomTaskAdminTable = ({ data, currentPage, handleDetail }) => {
  const pageNumber = (page, idx) => (page > 1 ? (page - 1) * 10 + idx : idx);

  const rows = 10 - data?.length;

  const detailActionButtonView = (name, status, isProcessor) => {
    if (name === 'Delete Permission' && status === 'selesai') return false;
    else if (status !== 'processed' || (status === 'processed' && isProcessor)) return handleDetail;
    else return false;
  };

  const detailDisabledButtonView = (name, status, isProcessor) => {
    if (name === 'Delete Permission' && status === 'selesai') return <DetailDisabledIcon />;
    else if (status !== 'processed' || (status === 'processed' && isProcessor)) return null;
    else return <DetailDisabledIcon />;
  };

  return (
    <div className={clsx('table')}>
      <table className="table-wrapper">
        <thead className="table-head">
          <tr>
            {column.map((item) => (
              <th key={item.value}>{item.header}</th>
            ))}
            <th className="action-col">Action</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {data?.length > 0 &&
            data.map((item, no) => (
              <tr key={item.id}>
                {column.map((col) => {
                  if (col.value === 'no') {
                    return (
                      <td
                        key={col.value}
                        data-type="no"
                        className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                      >
                        {pageNumber(currentPage, no + 1)}
                      </td>
                    );
                  } else if (col.value === 'status') {
                    return (
                      <td
                        key={col.value}
                        className={`pre-white-space address_details ${
                          col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''
                        }`}
                      >
                        {<StatusButton status={item[col.value].toLowerCase()} />}
                      </td>
                    );
                  } else {
                    return (
                      <td
                        key={col.value}
                        data-type={col.value}
                        className={col?.highlight_color ? `col-highlight-${col?.highlight_color}` : ''}
                      >
                        {item[col.value]}
                      </td>
                    );
                  }
                })}
                <td className="btn-action" data-type="actionButtons" data-key={!item.id ? item.code : item.id}>
                  <ActionButtons detail={detailActionButtonView(item.name, item.status, item.is_processor)} />
                  {detailDisabledButtonView(item.name, item.status, item.is_processor)}
                </td>
              </tr>
            ))}
          {rows > 0 &&
            [...Array(rows)].map((item) => (
              <tr key={item}>
                {column.map((col) => (
                  <td key={col}></td>
                ))}
                <td className="btn-action"></td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

const TaskAdminComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: approvalList, isLoading } = useSelector((state) => state.approvals);
  const user = useSelector((state) => state.user.profile);

  const isDriverDeleteApproval = useFliptBoolean('FLAG_FE_DELETE_DRIVER_APPROVAL', false, user.id || 'anonymous');

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(
      getAllApprovalData({
        page: currentPage,
        limit: 10,
      }),
    );
  }, [currentPage]);

  const handleDetail = (e) => {
    const approvalId = e.target.parentElement.parentElement.dataset['key'];
    const filteredOrderById = approvalList.data.filter((item) => item.id === +approvalId);

    const approvalReferenceType = filteredOrderById[0]?.approval?.reference_type;

    let params = {
      id: filteredOrderById[0].approval_id,
    };

    if (isDriverDeleteApproval) {
      if (approvalReferenceType === 'order') {
        params = { ...params, customerId: filteredOrderById[0]?.approval?.data?.customer_id };
      }
      params = { ...params, type: approvalReferenceType };
    } else {
      params = { ...params, customerId: filteredOrderById[0]?.approval?.data?.customer_id };
    }

    const taskDetailPathname =
      isDriverDeleteApproval && approvalReferenceType === 'user'
        ? `detail/${filteredOrderById[0]?.approval?.data?.id}`
        : `detail/${filteredOrderById[0].approval?.data?.transaction_key}`;

    navigate({
      pathname: taskDetailPathname,
      search: createSearchParams(params).toString(),
    });
  };

  const getStatusData = (status) => {
    if (status === 'pending') return 'baru';
    else if (status === 'rejected') return 'ditolak';
    else if (status === 'approved') return 'selesai';
    else return status;
  };

  const data =
    approvalList?.data?.map((item) => ({
      id: item.id,
      user_name:
        (isDriverDeleteApproval
          ? item.approval?.data?.user_name ?? item.approval?.data?.name
          : item.approval?.data?.user_name) ?? '-',
      name: item.approval?.approval_setting?.description.includes('update order') ? 'Recalculate' : 'Delete Permission',
      status: getStatusData(item.status),
      created_by: item.created_by,
      is_processor: item.is_processor,
    })) || [];

  const taskListDataView = () => {
    if (data?.length === 0) {
      return <EmptyState />;
    } else {
      return <CustomTaskAdminTable data={data} currentPage={currentPage} handleDetail={handleDetail} />;
    }
  };

  return (
    <div className="task-admin">
      <div className="task-admin__wrapper">
        <div className="task-admin__header">
          <div className="task-admin__title">
            <TaskAdminIcon fill="#009EF7" width="25px" height="25px" />
            <h2>Task Admin</h2>
          </div>
        </div>

        {isLoading ? <LoadingSpinner /> : taskListDataView()}

        <PaginationTable
          currentPage={currentPage}
          onPageChange={(newPage) => setCurrentPage(newPage)}
          totalCount={approvalList.pagination?.total}
        />
      </div>
    </div>
  );
};

export default TaskAdminComponent;
